import { ConfirmModal } from "../../../../../../../shared/components/dialogs/ConfirmModal"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Checkbox, FormControlLabel, Stack, Tooltip } from "@mui/material"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useState } from "react"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { MenuMeeting } from "../../../weekMenuTypes"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock } from "@fortawesome/pro-light-svg-icons"
import { deleteMeetingServiceOrders } from "../../../../../../merchant/merchant/production/meetingService/meetingServiceApi"

const useStyles = makeStyles()(() => ({
    card: {
        borderRadius: ".5rem",
        background: tailwindPalette.slate[100],
        padding: ".5rem 1rem",
    },
    lockIcon: {
        padding: "9px 14px",
    },
    lockContainer: {
        marginLeft: "-11px",
        marginRight: "1rem",
    },
    deleteOrdersClassNameOverride: {
        paddingBottom: "3.5rem",
    },
}))

export function UserDeleteMeetingDialog({
    open,
    onClose,
    meeting,
    disabledDeleteMeetingService,
    onSuccess,
    onError,
    disabledDeleteBooking,
}: {
    open: boolean
    onClose: () => void
    onError: (error: { title: string; description: string }) => void
    onSuccess: (data?: any) => void
    disabledDeleteMeetingService?: string
    disabledDeleteBooking?: string
    meeting: MenuMeeting
}) {
    const { classes } = useStyles()

    const { l10n } = useAppLocalization()
    const hasBooking = meeting.orderSummary.hasBooking
    const hasCatering = meeting.orderSummary.hasCatering

    const [deleteState, setDeleteState] = useState({
        deleteBooking: hasBooking && !disabledDeleteBooking,
        deleteMeetingService: hasCatering && !disabledDeleteMeetingService,
    })

    const { mutate: deleteOrder } = useApiMutation(deleteMeetingServiceOrders, {
        async onSuccess(_, data, context) {
            // Check if response is an array and if status code is 200
            const isSuccess =
                Array.isArray(data?.orders) && data?.orders?.length > 0 && data?.orders?.[0]?.status?.code === 200
            if (isSuccess) {
                onSuccess(data)
            } else {
                onError({
                    title: l10n.getString("user-home-canteen-upcoming-orders-delete-modal-error"),
                    description: data?.orders?.[0]?.status?.text || "Unknown error occurred",
                })
            }
        },
        async onError(_, error) {
            const errorMsg =
                error && typeof error === "object" && "displayMessage" in error
                    ? (error?.displayMessage as string)
                    : "Something went wrong. Try again."
            onError({
                title: l10n.getString("user-home-canteen-upcoming-orders-delete-modal-error"),
                description: errorMsg,
            })
        },
    })

    const handleDeleteClick = () => {
        deleteOrder({
            orderIds: [meeting.id],
            cancelResourceBooking: hasBooking ? deleteState.deleteBooking : false,
            cancelMeetingService: hasCatering ? deleteState.deleteMeetingService : false,
        })
    }

    const showCheckbox = meeting.orderSummary.hasBooking && meeting.orderSummary.hasCatering
    const isMeetingService = meeting.orderSummary.hasCatering && !meeting.orderSummary.hasBooking
    const itemType = isMeetingService ? "order" : "booking"

    return (
        <LocalizedStrict id="user-home-canteen-upcoming-orders-delete-modal-title" attrs={{ typeOfEntity: true }}>
            <ConfirmModal
                open={open}
                onClose={onClose}
                autoCloseAfterSubmit={false}
                title={
                    <LocalizedStrict
                        id="delete-confirm-modal-title"
                        vars={{
                            itemType,
                        }}
                    >
                        <span>Delete {itemType}</span>
                    </LocalizedStrict>
                }
                message={
                    <LocalizedStrict
                        id="delete-confirm-modal-question"
                        vars={{
                            itemName: meeting.meetingDetails.meetingName,
                        }}
                    >
                        <>{`Are you sure you want to delete ${meeting.meetingDetails.meetingName}?`}</>
                    </LocalizedStrict>
                }
                buttonSubmitDisabled={!deleteState.deleteBooking && !deleteState.deleteMeetingService}
                handleClick={handleDeleteClick}
                buttonLabelSubmit={l10n.getString("delete-confirm-modal-button-delete")}
                hideCloseIcon={false}
                classNameOverride={classes.deleteOrdersClassNameOverride}
            >
                {showCheckbox && (
                    <Stack className={classes.card} direction="column" justifyContent="center">
                        {disabledDeleteBooking ? (
                            <Tooltip title={disabledDeleteBooking}>
                                <Stack direction="row" alignItems="center" className={classes.lockContainer}>
                                    <FontAwesomeIcon icon={faLock} className={classes.lockIcon} />
                                    <LocalizedStrict id="meeting-service-delete-orders-checkbox-label-booking-lock">
                                        <>Cancel resource booking</>
                                    </LocalizedStrict>
                                </Stack>
                            </Tooltip>
                        ) : (
                            <LocalizedStrict
                                id="user-home-canteen-upcoming-orders-delete-modal-booking-label"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={deleteState.deleteBooking}
                                            disabled={!hasBooking}
                                            onChange={(event) => {
                                                setDeleteState((prev) => ({
                                                    ...prev,
                                                    deleteBooking: event.target.checked,
                                                }))
                                            }}
                                        />
                                    }
                                    label="Delete booking"
                                />
                            </LocalizedStrict>
                        )}
                        {disabledDeleteMeetingService ? (
                            <Tooltip title={disabledDeleteMeetingService}>
                                <Stack direction="row" alignItems="center" className={classes.lockContainer}>
                                    <FontAwesomeIcon icon={faLock} className={classes.lockIcon} />
                                    <LocalizedStrict id="meeting-service-delete-orders-checkbox-label-meeting-service-cancel-lock">
                                        <>Cancel meeting service</>
                                    </LocalizedStrict>
                                </Stack>
                            </Tooltip>
                        ) : (
                            <LocalizedStrict
                                id="user-home-canteen-upcoming-orders-delete-modal-meeting-label"
                                attrs={{ label: true }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={deleteState.deleteMeetingService}
                                            disabled={!hasCatering}
                                            onChange={(event) => {
                                                setDeleteState((prev) => ({
                                                    ...prev,
                                                    deleteMeetingService: event.target.checked,
                                                }))
                                            }}
                                        />
                                    }
                                    label="Delete meeting service"
                                />
                            </LocalizedStrict>
                        )}
                    </Stack>
                )}
            </ConfirmModal>
        </LocalizedStrict>
    )
}
